/**
 * 🔒 Protected Route
 *
 * يحمي صفحات الـ dashboard من الوصول بدون auth.
 *
 * 🛡️ Security Layers (C-9 fix):
 *   1. isLoading: نتأكد إن Firebase انتهى من التحقق
 *   2. Firebase auth.currentUser: السلطة الوحيدة (مش localStorage)
 *   3. user من Zustand store: للـ role + permissions (من Custom Claims)
 *
 *   حتى لو مهاجم زوّر localStorage، getAuth().currentUser هو الحقيقة.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { getAuth } from 'firebase/auth';

import { useAuthStore } from '@application/stores/auth.store';

export function ProtectedRoute() {
  const { isAuthenticated, isLoading, isHydrated, user } = useAuthStore();
  const location = useLocation();

  // أثناء التحقق من session
  if (!isHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-royal">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-white text-center"
        >
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
          <p className="text-sm opacity-80">جاري التحقق من الجلسة...</p>
        </motion.div>
      </div>
    );
  }

  // 🛡️ (C-9 fix) Double-check ضد Firebase auth.currentUser
  //    لو الـ store يقول authenticated لكن Firebase ما عنده user → reject
  //    (يحمي من localStorage tampering)
  const firebaseUser = getAuth().currentUser;
  if (!isAuthenticated || !firebaseUser) {
    return <Navigate to="/login" replace />;
  }

  // ⚠️ Sanity check: تطابق الـ uid (دفاع إضافي)
  if (user && user.uid !== firebaseUser.uid) {
    // 🚨 mismatch — قد يكون tampering
    // eslint-disable-next-line no-console
    console.warn('[ProtectedRoute] UID mismatch detected, forcing logout');
    return <Navigate to="/login" replace />;
  }

  // 🆕 (Phase 4) إجبار تغيير كلمة السر للـ provider users
  //   - لو mustChangePassword=true → احوّله لـ /change-password
  //   - لو هو في /change-password بالفعل → اتركه
  if (
    user?.mustChangePassword === true &&
    location.pathname !== '/change-password'
  ) {
    return <Navigate to="/change-password" replace />;
  }

  return <Outlet />;
}
